import { useState, useEffect } from "react";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Calculator,
  Plus,
  Trash2,
  AlertTriangle,
  CheckCircle2,
  Info,
  Zap,
  Radio,
  Lightbulb,
  Download,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { systemsAPI, equipmentAPI } from "@/lib/api";

export default function IntermodulationPage({ user, onLogout }) {
  const [frequencies, setFrequencies] = useState([""]);
  const [results, setResults] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [systems, setSystems] = useState([]);
  const [equipmentList, setEquipmentList] = useState([]);
  const [loadingSystems, setLoadingSystems] = useState(false);

  // Fetch user's systems and equipment on mount
  useEffect(() => {
    const fetchData = async () => {
      setLoadingSystems(true);
      try {
        const [systemsRes, equipmentRes] = await Promise.all([
          systemsAPI.getAll(),
          equipmentAPI.getAll()
        ]);
        setSystems(systemsRes.data || []);
        // Equipment API returns {equipment: [...]}
        const equipData = equipmentRes.data?.equipment || equipmentRes.data || [];
        setEquipmentList(Array.isArray(equipData) ? equipData : []);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoadingSystems(false);
      }
    };
    fetchData();
  }, []);

  // Find equipment presets for a given frequency
  const findEquipmentForFrequency = (frequency) => {
    // Find which system uses this frequency
    const system = systems.find(s => Math.abs(s.frequency - frequency) < 0.01);
    if (!system) return null;
    
    // Find the equipment details - ensure equipmentList is an array
    const equipList = Array.isArray(equipmentList) ? equipmentList : [];
    const equipment = equipList.find(e => e.id === system.equipment_id);
    return { system, equipment };
  };

  // Suggest alternative frequency from the same equipment
  const suggestAlternativeFrequency = (conflictFrequency, allUsedFrequencies) => {
    const equipmentInfo = findEquipmentForFrequency(conflictFrequency);
    if (!equipmentInfo || !equipmentInfo.equipment) {
      return null;
    }
    
    const { system, equipment } = equipmentInfo;
    const suggestions = [];
    
    // Check preset frequencies from the equipment
    if (equipment.preset_frequencies) {
      for (const [group, channels] of Object.entries(equipment.preset_frequencies)) {
        for (const [channel, freq] of Object.entries(channels)) {
          const presetFreq = parseFloat(freq);
          // Check if this preset is not already in use
          const isUsed = allUsedFrequencies.some(f => Math.abs(f - presetFreq) < 0.05);
          if (!isUsed && presetFreq >= system.band_min && presetFreq <= system.band_max) {
            suggestions.push({
              frequency: presetFreq,
              group: group,
              channel: channel,
              equipment: equipment.full_name || `${equipment.brand} ${equipment.model}`
            });
          }
        }
      }
    }
    
    // Sort by frequency proximity to original
    suggestions.sort((a, b) => 
      Math.abs(a.frequency - conflictFrequency) - Math.abs(b.frequency - conflictFrequency)
    );
    
    return suggestions.length > 0 ? suggestions[0] : null;
  };

  // Load frequencies from user's systems
  const loadFromSystems = () => {
    if (systems.length === 0) {
      toast.error("No tienes sistemas registrados");
      return;
    }
    const systemFreqs = systems.map(s => s.frequency.toFixed(3));
    setFrequencies(systemFreqs);
    toast.success(`${systemFreqs.length} frecuencias cargadas de tus sistemas`);
  };

  const addFrequency = () => {
    if (frequencies.length < 20) {
      setFrequencies([...frequencies, ""]);
    } else {
      toast.error("Máximo 20 frecuencias permitidas");
    }
  };

  const removeFrequency = (index) => {
    if (frequencies.length > 1) {
      const newFreqs = frequencies.filter((_, i) => i !== index);
      setFrequencies(newFreqs);
    }
  };

  const updateFrequency = (index, value) => {
    const newFreqs = [...frequencies];
    newFreqs[index] = value;
    setFrequencies(newFreqs);
  };

  const calculateIMD = () => {
    // Parse and validate frequencies
    const validFreqs = frequencies
      .map(f => parseFloat(f))
      .filter(f => !isNaN(f) && f > 0)
      .sort((a, b) => a - b);

    if (validFreqs.length < 2) {
      toast.error("Se necesitan al menos 2 frecuencias válidas");
      return;
    }

    setAnalyzing(true);

    // Calculate IMD products
    const imdProducts = [];
    const conflicts = [];

    // Calculate 2nd order products (A + B, A - B)
    for (let i = 0; i < validFreqs.length; i++) {
      for (let j = i + 1; j < validFreqs.length; j++) {
        const a = validFreqs[i];
        const b = validFreqs[j];
        
        // 2nd order
        imdProducts.push({
          type: "2do Orden",
          formula: `${a.toFixed(3)} + ${b.toFixed(3)}`,
          result: a + b,
          order: 2
        });
        imdProducts.push({
          type: "2do Orden", 
          formula: `${b.toFixed(3)} - ${a.toFixed(3)}`,
          result: b - a,
          order: 2
        });
      }
    }

    // Calculate 3rd order products (2A - B, 2B - A)
    for (let i = 0; i < validFreqs.length; i++) {
      for (let j = 0; j < validFreqs.length; j++) {
        if (i !== j) {
          const a = validFreqs[i];
          const b = validFreqs[j];
          
          const imd3 = (2 * a) - b;
          if (imd3 > 0) {
            imdProducts.push({
              type: "3er Orden",
              formula: `2×${a.toFixed(3)} - ${b.toFixed(3)}`,
              result: imd3,
              order: 3
            });
          }
        }
      }
    }

    // Calculate 5th order products (3A - 2B)
    for (let i = 0; i < validFreqs.length; i++) {
      for (let j = 0; j < validFreqs.length; j++) {
        if (i !== j) {
          const a = validFreqs[i];
          const b = validFreqs[j];
          
          const imd5 = (3 * a) - (2 * b);
          if (imd5 > 0) {
            imdProducts.push({
              type: "5to Orden",
              formula: `3×${a.toFixed(3)} - 2×${b.toFixed(3)}`,
              result: imd5,
              order: 5
            });
          }
        }
      }
    }

    // Check for conflicts (IMD products within 0.1 MHz of any frequency)
    const tolerance = 0.1; // MHz
    for (const product of imdProducts) {
      for (const freq of validFreqs) {
        const diff = Math.abs(product.result - freq);
        if (diff <= tolerance && diff > 0) {
          // Find suggestion for this conflicting frequency
          const suggestion = suggestAlternativeFrequency(freq, validFreqs);
          
          conflicts.push({
            ...product,
            conflictWith: freq,
            distance: diff,
            suggestion: suggestion
          });
        }
      }
    }

    // Sort by order and result
    imdProducts.sort((a, b) => a.order - b.order || a.result - b.result);

    setTimeout(() => {
      setResults({
        frequencies: validFreqs,
        products: imdProducts,
        conflicts: conflicts,
        totalProducts: imdProducts.length,
        hasConflicts: conflicts.length > 0
      });
      setAnalyzing(false);
      
      if (conflicts.length > 0) {
        toast.warning(`Se encontraron ${conflicts.length} conflictos potenciales`);
      } else {
        toast.success("No se encontraron conflictos de intermodulación");
      }
    }, 500);
  };

  const clearAll = () => {
    setFrequencies([""]);
    setResults(null);
  };

  const loadExample = () => {
    // Example frequencies that will show some IMD conflicts
    setFrequencies(["614.000", "614.500", "615.000", "615.500", "616.000"]);
    toast.info("Frecuencias de ejemplo cargadas");
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-cyan-900/30 rounded-lg flex items-center justify-center">
              <Calculator className="w-5 h-5 text-cyan-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Calculadora de Intermodulación</h1>
              <p className="text-sm text-gray-400">Análisis de productos IMD de 2do, 3er y 5to orden</p>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Input Section */}
          <div className="space-y-4">
            <Card className="bg-[#111] border-white/10">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg text-white flex items-center gap-2">
                    <Radio className="w-5 h-5 text-cyan-400" />
                    Frecuencias a Analizar
                  </CardTitle>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={loadFromSystems}
                      disabled={loadingSystems || systems.length === 0}
                      className="text-xs border-green-500/30 text-green-400 hover:bg-green-500/10"
                      title={systems.length === 0 ? "No tienes sistemas registrados" : `Cargar ${systems.length} sistemas`}
                    >
                      <Download className="w-3 h-3 mr-1" />
                      Mis Sistemas ({systems.length})
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={loadExample}
                      className="text-xs border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10"
                    >
                      Cargar Ejemplo
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={clearAll}
                      className="text-xs border-red-500/30 text-red-400 hover:bg-red-500/10"
                    >
                      Limpiar
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {frequencies.map((freq, index) => (
                  <div key={`freq-input-${index}`} className="flex items-center gap-2">
                    <span className="text-xs text-gray-500 w-6">{index + 1}.</span>
                    <Input
                      type="number"
                      step="0.001"
                      placeholder="Ej: 614.500"
                      value={freq}
                      onChange={(e) => updateFrequency(index, e.target.value)}
                      className="bg-black/50 border-white/10 text-white"
                    />
                    <span className="text-xs text-gray-500">MHz</span>
                    {frequencies.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFrequency(index)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10 p-2"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}

                <div className="flex gap-2 pt-2">
                  <Button
                    variant="outline"
                    onClick={addFrequency}
                    disabled={frequencies.length >= 20}
                    className="flex-1 border-dashed border-white/20 text-gray-400 hover:text-white hover:border-cyan-500/50"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Agregar Frecuencia
                  </Button>
                </div>

                <Button
                  onClick={calculateIMD}
                  disabled={analyzing}
                  className="w-full bg-cyan-600 hover:bg-cyan-700 mt-4"
                  data-testid="calculate-imd-btn"
                >
                  {analyzing ? (
                    <>Analizando...</>
                  ) : (
                    <>
                      <Calculator className="w-4 h-4 mr-2" />
                      Calcular Intermodulación
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Info Card */}
            <Card className="bg-blue-900/20 border-blue-700/30">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                  <div className="text-sm">
                    <p className="text-blue-400 font-medium mb-1">¿Qué es la Intermodulación?</p>
                    <p className="text-gray-400 text-xs">
                      La intermodulación ocurre cuando dos o más frecuencias interactúan, 
                      creando productos adicionales. Los productos de <strong className="text-blue-300">3er orden (2A-B)</strong> son 
                      los más problemáticos en sistemas RF ya que caen cerca de las frecuencias originales.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Results Section */}
          <div className="space-y-4">
            {results ? (
              <>
                {/* Summary Card */}
                <Card className={`border ${results.hasConflicts ? 'bg-red-900/20 border-red-700/30' : 'bg-green-900/20 border-green-700/30'}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      {results.hasConflicts ? (
                        <AlertTriangle className="w-8 h-8 text-red-400" />
                      ) : (
                        <CheckCircle2 className="w-8 h-8 text-green-400" />
                      )}
                      <div>
                        <p className={`font-semibold ${results.hasConflicts ? 'text-red-400' : 'text-green-400'}`}>
                          {results.hasConflicts 
                            ? `${results.conflicts.length} Conflictos Detectados`
                            : 'Sin Conflictos'}
                        </p>
                        <p className="text-xs text-gray-400">
                          {results.frequencies.length} frecuencias analizadas • {results.totalProducts} productos IMD calculados
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Conflicts Detail */}
                {results.hasConflicts && (
                  <Card className="bg-[#111] border-red-500/30">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base text-red-400 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        Conflictos de Intermodulación
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3 max-h-[300px] overflow-y-auto">
                      {results.conflicts.map((conflict, conflictIndex) => (
                        <div key={`conflict-${conflict.result}-${conflictIndex}`} className="p-3 bg-red-900/20 rounded border border-red-500/20">
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <Badge className="bg-red-500/20 text-red-400 text-[10px] mb-1">
                                {conflict.type}
                              </Badge>
                              <p className="text-xs text-gray-300 font-mono">
                                {conflict.formula} = <strong className="text-red-400">{conflict.result.toFixed(3)} MHz</strong>
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-[10px] text-gray-500">Conflicto con</p>
                              <p className="text-xs text-red-400 font-mono">{conflict.conflictWith.toFixed(3)} MHz</p>
                              <p className="text-[10px] text-gray-500">±{(conflict.distance * 1000).toFixed(0)} kHz</p>
                            </div>
                          </div>
                          
                          {/* Suggestion from same equipment */}
                          {conflict.suggestion ? (
                            <div className="mt-2 p-2 bg-green-900/30 border border-green-500/30 rounded">
                              <div className="flex items-center gap-2 text-xs">
                                <Lightbulb className="w-4 h-4 text-green-400" />
                                <span className="text-green-400 font-medium">Sugerencia del mismo equipo:</span>
                              </div>
                              <div className="mt-1 flex items-center justify-between">
                                <div>
                                  <p className="text-sm font-mono text-green-300">
                                    {conflict.suggestion.frequency.toFixed(3)} MHz
                                  </p>
                                  <p className="text-[10px] text-gray-400">
                                    Grupo {conflict.suggestion.group} • Canal {conflict.suggestion.channel}
                                  </p>
                                </div>
                                <Badge className="bg-green-500/20 text-green-300 text-[10px]">
                                  {conflict.suggestion.equipment}
                                </Badge>
                              </div>
                            </div>
                          ) : (
                            <div className="mt-2 p-2 bg-yellow-900/20 border border-yellow-500/20 rounded">
                              <p className="text-[10px] text-yellow-400 flex items-center gap-1">
                                <Info className="w-3 h-3" />
                                Sin preset disponible. Considera usar otra frecuencia manualmente.
                              </p>
                            </div>
                          )}
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}

                {/* All Products */}
                <Card className="bg-[#111] border-white/10">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base text-white flex items-center gap-2">
                      <Zap className="w-4 h-4 text-cyan-400" />
                      Productos de Intermodulación
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="max-h-[300px] overflow-y-auto space-y-1">
                      {/* Group by order */}
                      {[3, 5, 2].map(order => {
                        const orderProducts = results.products.filter(p => p.order === order);
                        if (orderProducts.length === 0) return null;
                        
                        const orderColors = {
                          2: { bg: 'bg-gray-500/10', text: 'text-gray-400', border: 'border-gray-500/20' },
                          3: { bg: 'bg-orange-500/10', text: 'text-orange-400', border: 'border-orange-500/20' },
                          5: { bg: 'bg-purple-500/10', text: 'text-purple-400', border: 'border-purple-500/20' }
                        };
                        const colors = orderColors[order];
                        
                        return (
                          <div key={order} className="mb-3">
                            <p className={`text-xs font-medium ${colors.text} mb-1`}>
                              {order === 2 ? '2do' : order === 3 ? '3er' : '5to'} Orden ({orderProducts.length})
                            </p>
                            <div className="grid grid-cols-2 gap-1">
                              {orderProducts.slice(0, 20).map((product, idx) => {
                                const isConflict = results.conflicts.some(
                                  c => c.formula === product.formula
                                );
                                return (
                                  <div 
                                    key={idx} 
                                    className={`p-1.5 rounded text-xs font-mono ${
                                      isConflict 
                                        ? 'bg-red-900/30 border border-red-500/30' 
                                        : `${colors.bg} border ${colors.border}`
                                    }`}
                                  >
                                    <span className={isConflict ? 'text-red-400' : 'text-gray-400'}>
                                      {product.result.toFixed(3)}
                                    </span>
                                    {isConflict && <AlertTriangle className="w-3 h-3 inline ml-1 text-red-400" />}
                                  </div>
                                );
                              })}
                            </div>
                            {orderProducts.length > 20 && (
                              <p className="text-[10px] text-gray-500 mt-1">
                                +{orderProducts.length - 20} más...
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                {/* Input Frequencies */}
                <Card className="bg-[#111] border-white/10">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-white">Frecuencias Analizadas</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {results.frequencies.map((freq, idx) => (
                        <Badge 
                          key={idx} 
                          className="bg-cyan-900/30 text-cyan-400 border border-cyan-500/30 font-mono"
                        >
                          {freq.toFixed(3)} MHz
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card className="bg-[#111] border-white/10">
                <CardContent className="p-12 text-center">
                  <Calculator className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400 mb-2">Ingresa al menos 2 frecuencias</p>
                  <p className="text-xs text-gray-600">
                    Los resultados aparecerán aquí después del cálculo
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
